import {
  getCookie,
  setStrictlyNecessaryCookie,
} from '@atlassian/browser-storage-controls';

export type PipelineEnvOption = 'ddev' | 'stg' | '';

export const FRONTBUCKET_VERSION_COOKIE = 'bbc-frontbucket-version';
export const FRONTBUCKET_DEV_SERVER_URL_COOKIE =
  'bbc-frontbucket-dev-server-url';
export const FRONTBUCKET_PIPELINES_ENV_COOKIE = 'bbc-frontbucket-pipelines-env';
export const FRONTBUCKET_PIPELINES_DEBUG_COOKIE =
  'bbc-frontbucket-pipelines-debug';

export type CookieValue =
  | 'server'
  | 'local'
  | 'devbox'
  | 'dev'
  | 'canary'
  | 'production'
  | 'experiment'
  | 'staging'
  | ['bifrost-pr', string];

export const getCookieValue = (): CookieValue => {
  const cookie = getCookie(FRONTBUCKET_VERSION_COOKIE);

  if (!cookie) {
    return 'server';
  }
  if (cookie === 'local') {
    return 'local';
  }
  if (cookie === 'devbox') {
    return 'devbox';
  }
  if (cookie === 'canary') {
    return 'canary';
  }
  if (cookie === 'dev') {
    return 'dev';
  }
  if (cookie === 'production') {
    return 'production';
  }
  if (cookie === 'experiment') {
    return 'experiment';
  }
  if (cookie === 'staging') {
    return 'staging';
  }
  const bifrostHashMatch = /bifrost_([a-f0-9]{40})/.exec(cookie);
  if (bifrostHashMatch) {
    const bifrostHash = bifrostHashMatch[1];
    return ['bifrost-pr', bifrostHash];
  }
  // Fall back to server
  return 'server';
};

export const getDevServerUrlCookieValue = (): string =>
  getCookie(FRONTBUCKET_DEV_SERVER_URL_COOKIE) || '';

export const getPipelinesEnvCookieValue = (): PipelineEnvOption => {
  const cookie = getCookie(FRONTBUCKET_PIPELINES_ENV_COOKIE);

  if (cookie === 'ddev' || cookie === 'stg') {
    return cookie;
  }

  return '';
};

/**
 * Sets the bbc-frontbucket-version cookie to load assets built from a specific
 * PR commit. Defaults to a 12-hour expiry if none is provided.
 */
export const setBifrostPRCookie = (commitHash: string, expires?: number) => {
  setStrictlyNecessaryCookie(
    FRONTBUCKET_VERSION_COOKIE,
    `bifrost_${commitHash}`,
    { path: '/', expires }
  );
};

/**
 * Sets the bbc-frontbucket-version cookie to a named asset location
 * (e.g. 'local', 'canary', 'dev', 'staging', etc).
 */
export const setFrontbucketVersionCookie = (
  value: string,
  expires: number | undefined
) => {
  setStrictlyNecessaryCookie(FRONTBUCKET_VERSION_COOKIE, value, {
    path: '/',
    expires,
  });
};

export const setFrontbucketDevServerUrlCookie = (
  value: string,
  expires: number | undefined
) => {
  setStrictlyNecessaryCookie(FRONTBUCKET_DEV_SERVER_URL_COOKIE, value, {
    path: '/',
    expires,
  });
};

/**
 * Sets the bbc-frontbucket-pipelines-env cookie.
 */
export const setPipelinesEnvCookie = (
  value: PipelineEnvOption,
  expires: number | undefined
) => {
  setStrictlyNecessaryCookie(FRONTBUCKET_PIPELINES_ENV_COOKIE, value, {
    path: '/',
    expires,
  });
};

/**
 * Sets the bbc-frontbucket-pipelines-debug cookie.
 */
export const setPipelinesDebugCookie = (
  enabled: boolean,
  expires: number | undefined
) => {
  setStrictlyNecessaryCookie(
    FRONTBUCKET_PIPELINES_DEBUG_COOKIE,
    enabled ? 'on' : '',
    { path: '/', expires }
  );
};
